import { create } from 'zustand';
import { toUiError } from './errors.ts';
import type { UiError } from './errors.ts';

export type ToastTone = 'neutral' | 'success' | 'error' | 'working';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastItem {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
  action?: ToastAction;
  /** ms; 0 keeps it until dismissed. */
  duration: number;
  createdAt: number;
}

export interface ToastInput {
  title: string;
  description?: string;
  tone?: ToastTone;
  action?: ToastAction;
  duration?: number;
  /** Replace an existing toast with this id (for progress then result). */
  id?: string;
}

interface ToastStore {
  toasts: ToastItem[];
  push: (t: ToastInput) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

/** At most 3 visible (component token toast.max-visible): a fourth pushes out the oldest. */
const MAX_TOASTS = 3;
let seq = 0;

export const useToasts = create<ToastStore>()((set) => ({
  toasts: [],
  push: (t) => {
    const id = t.id ?? `t${++seq}`;
    const tone = t.tone ?? 'neutral';
    const item: ToastItem = {
      id,
      tone,
      title: t.title,
      description: t.description,
      action: t.action,
      duration: t.duration ?? (tone === 'error' ? 8000 : tone === 'working' ? 0 : t.action ? 10_000 : 5000),
      createdAt: Date.now(),
    };
    set((s) => {
      const rest = s.toasts.filter((x) => x.id !== id);
      return { toasts: [...rest, item].slice(-MAX_TOASTS) };
    });
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) })),
  clear: () => set({ toasts: [] }),
}));

/**
 * Show a toast. Rendered by <Toaster /> in the app shell.
 *
 *   toast('Copied');
 *   toast.success('Session started', 'Claude is working in qawl/main.');
 *   toast.error(err);                      // anything thrown; shows what + why + next
 *   toast('Worktree removed', { action: { label: 'Undo', onClick: restore } });
 *   const id = toast.working('Stopping session'); ...; toast.success('Session stopped', undefined, id);
 */
export function toast(title: string, opts: Omit<ToastInput, 'title'> = {}): string {
  return useToasts.getState().push({ title, ...opts });
}

toast.success = (title: string, description?: string, id?: string): string =>
  useToasts.getState().push({ id, title, description, tone: 'success' });

toast.error = (error: unknown, id?: string): string => {
  const e: UiError = toUiError(error);
  return useToasts.getState().push({ id, title: e.what, description: `${e.why} ${e.next}`.trim(), tone: 'error' });
};

toast.working = (title: string, description?: string): string =>
  useToasts.getState().push({ title, description, tone: 'working', duration: 0 });

toast.dismiss = (id: string): void => useToasts.getState().dismiss(id);
